const BG_ASSET_BASE = `${process.env.PUBLIC_URL ?? ''}/assets/bg`;
const MENU_VOTE_ASSET_BASE = `${process.env.PUBLIC_URL ?? ''}/assets/menu-vote`;
const MENU_RESULT_ASSET_BASE = `${process.env.PUBLIC_URL ?? ''}/assets/menu-result`;

/** hero 배경 구름 (assets/bg) */
export const BG_CLOUDS = {
  cloudBig: `${BG_ASSET_BASE}/cloud_big.png`,
  cloudSmall: `${BG_ASSET_BASE}/cloud_small.png`,
} as const;

export interface CloudInSetConfig {
  /** hero 높이(812px) 대비 세로 위치 % */
  topPercent: number;
  offsetX: number;
  width: number;
  opacity?: number;
}

export interface CloudSetConfig {
  id: string;
  /** 세트 내 작·큰 구름 공통 시작 시점 */
  delay: string;
  small: CloudInSetConfig;
  big: CloudInSetConfig;
}

export const CLOUD_SMALL_CYCLE_S = 48;
/** 큰 구름 = 작은 구름 속도의 1/2.5 → 주기 2.5배 */
export const CLOUD_BIG_SPEED_RATIO = 2.5;
export const CLOUD_SMALL_CYCLE = `${CLOUD_SMALL_CYCLE_S}s`;
export const CLOUD_BIG_CYCLE = `${CLOUD_SMALL_CYCLE_S * CLOUD_BIG_SPEED_RATIO}s`;

export const CLOUD_SET_TRACK_WIDTH = 420;

/** 디자인 hero 812px 기준 px → top % */
export const heroTopPercent = (px: number) => Math.round((px / 812) * 1000) / 10;

function setWidth(small: CloudInSetConfig, big: CloudInSetConfig) {
  return Math.max(small.offsetX + small.width, big.offsetX + big.width);
}

/** 구름 세트 2개(4구름) */
export const CLOUD_SETS: CloudSetConfig[] = [
  {
    id: 'set-a',
    delay: '-12s',
    small: { topPercent: heroTopPercent(258), offsetX: 12, width: 96, opacity: 0.92 },
    big: { topPercent: heroTopPercent(392), offsetX: 48, width: 368, opacity: 0.95 },
  },
  {
    id: 'set-b',
    delay: '-36s',
    small: { topPercent: heroTopPercent(236), offsetX: 28, width: 114 },
    big: { topPercent: 62, offsetX: 0, width: 302, opacity: 0.9 },
  },
];

export const CLOUD_FIXED = {
  left: 24,
  set: CLOUD_SETS[0],
} as const;

export function getCloudSetWidth(set: CloudSetConfig) {
  return Math.max(CLOUD_SET_TRACK_WIDTH, setWidth(set.small, set.big));
}

/** 투표·결과 화면 공통 hero 장식 (단풍·바람 등) */
export const SHARED_HERO_DECORATIONS = {
  decorationMaple: `${MENU_VOTE_ASSET_BASE}/decoration-maple.png`,
  decorationMapleBig: `${MENU_VOTE_ASSET_BASE}/decoration-maple-big.png`,
  /** Figma 98:659 — hero 바람(연기) 장식 */
  rewardWind: `${MENU_VOTE_ASSET_BASE}/reward-decoration.png`,
} as const;

export interface HeroWindConfig {
  left: number;
  topPercent: number;
  width: number;
  opacity?: number;
}

/** reward-decoration.png — hero 고정 바람 장식 */
export const HERO_WIND: HeroWindConfig = {
  left: 111,
  topPercent: heroTopPercent(199),
  width: 308,
  opacity: 0.85,
};

/** 결과 화면 hero 전용 장식 */
export const RESULT_HERO_DECORATIONS = {
  decorationMapleAlt: `${MENU_RESULT_ASSET_BASE}/decoration-maple-alt.png`,
} as const;

export interface MapleDecoConfig {
  id: string;
  left: number;
  topPercent: number;
  width: number;
  rotate?: number;
  zIndex?: number;
  opacity?: number;
  /** true면 위치·회전만 고정 표시 */
  static?: boolean;
  duration?: string;
  ease?: string;
  delay?: string;
  /** 바람 시작(오른쪽) X 오프셋 */
  windFromX?: number;
  windMidX?: number;
  /** 바람 끝(왼쪽) X 오프셋 */
  windToX?: number;
  windDropEnd?: number;
}

/** 단풍 — 오른쪽에서 왼쪽으로 불어 opacity로 나타났다 사라짐 */
export const MAPLE_DECOS: {
  voteBig: MapleDecoConfig;
  vote: MapleDecoConfig;
  result: MapleDecoConfig;
  resultAlt: MapleDecoConfig;
} = {
  voteBig: {
    id: 'maple-vote-big',
    left: -58,
    topPercent: heroTopPercent(24),
    width: 178,
    rotate: -118,
    zIndex: 2,
    opacity: 1,
    static: true,
  },
  vote: {
    id: 'maple-vote',
    left: 62,
    topPercent: heroTopPercent(178),
    width: 51,
    rotate: -103,
    zIndex: 2,
    duration: '8s',
    ease: 'cubic-bezier(0.45, 0.05, 0.55, 0.95)',
    delay: '0s',
    windFromX: 38,
    windMidX: -6,
    windToX: -58,
    windDropEnd: 22,
    opacity: 1,
  },
  result: {
    id: 'maple-result',
    left: 78,
    topPercent: heroTopPercent(198),
    width: 40,
    rotate: -103,
    duration: '9s',
    ease: 'cubic-bezier(0.33, 0.12, 0.25, 1)',
    delay: '-2.5s',
    windFromX: 32,
    windMidX: -10,
    windToX: -52,
    windDropEnd: 26,
    opacity: 1,
  },
  resultAlt: {
    id: 'maple-result-alt',
    left: 225,
    topPercent: heroTopPercent(-85),
    width: 277,
    rotate: -120,
    duration: '12s',
    ease: 'cubic-bezier(0.25, 0.8, 0.35, 1)',
    delay: '-4s',
    windFromX: 72,
    windMidX: -24,
    windToX: -110,
    windDropEnd: 40,
    opacity: 1,
  },
};
